import { useEffect, useMemo, useRef, useState } from 'react';

type StreamRow = {
  canId: string;
  dlc: number;
  data: string;
  freq: string;
  known: boolean;
  signalLabel?: string;
  pulseKey: number;
};

type RowSeed = {
  canId: string;
  dlc: number;
  pattern: () => string;
  freq: string;
  known: boolean;
  signalLabel?: string;
};

const HEX = '0123456789ABCDEF';

function randHexByte() {
  return HEX[Math.floor(Math.random() * 16)] + HEX[Math.floor(Math.random() * 16)];
}

function bytes(count: number) {
  const out: string[] = [];
  for (let index = 0; index < count; index += 1) out.push(randHexByte());
  return out.join(' ');
}

function counterByte(state: { value: number }) {
  state.value = (state.value + 1) & 0xff;
  return state.value.toString(16).toUpperCase().padStart(2, '0');
}

const obdCounter = { value: 0 };
const ecuCounter = { value: 0x10 };

const ROW_SEEDS: RowSeed[] = [
  {
    canId: '0x7E8',
    dlc: 8,
    known: true,
    signalLabel: 'OBD-II RPM',
    freq: '12.4 Hz',
    pattern: () => `04 41 0C ${randHexByte()} ${randHexByte()} 00 00 ${counterByte(obdCounter)}`
  },
  {
    canId: '0x18FEF100',
    dlc: 8,
    known: true,
    signalLabel: 'J1939 Speed',
    freq: '98.4 Hz',
    pattern: () => `${randHexByte()} 00 ${randHexByte()} 91 0A FF 21 ${randHexByte()}`
  },
  {
    canId: '0x0CF00400',
    dlc: 8,
    known: true,
    signalLabel: 'EEC1 Engine',
    freq: '250 Hz',
    pattern: () => `62 ${randHexByte()} 81 00 C4 33 ${randHexByte()} 90`
  },
  {
    canId: '0x626',
    dlc: 8,
    known: false,
    freq: '50.0 Hz',
    pattern: () => `${randHexByte()} ${randHexByte()} 11 22 ${randHexByte()} 00 7C 2D`
  },
  {
    canId: '0x321',
    dlc: 6,
    known: false,
    freq: '20.0 Hz',
    pattern: () => `10 02 A4 ${randHexByte()} 09 ${randHexByte()}`
  },
  {
    canId: '0x123',
    dlc: 8,
    known: false,
    freq: '44.1 Hz',
    pattern: () => `04 ${randHexByte()} 00 ${counterByte(ecuCounter)} 00 00 ${randHexByte()} ${randHexByte()}`
  },
  {
    canId: '0x2A4',
    dlc: 4,
    known: false,
    freq: '8.0 Hz',
    pattern: () => bytes(4)
  }
];

function seedRow(seed: RowSeed, pulseKey: number): StreamRow {
  return {
    canId: seed.canId,
    dlc: seed.dlc,
    data: seed.pattern(),
    freq: seed.freq,
    known: seed.known,
    signalLabel: seed.signalLabel,
    pulseKey
  };
}

export function HeroCanStream() {
  const initialRows = useMemo(() => ROW_SEEDS.map((seed, index) => seedRow(seed, index)), []);
  const [rows, setRows] = useState<StreamRow[]>(initialRows);
  const [tick, setTick] = useState(0);
  const [framesSeen, setFramesSeen] = useState(2_847_120);
  const [fps, setFps] = useState(1248);
  const [paused, setPaused] = useState(false);
  const pulseCounter = useRef(initialRows.length);
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (paused) {
      return () => {
        if (intervalRef.current !== null) {
          window.clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    }

    const reduceMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    intervalRef.current = window.setInterval(() => {
      setRows((current) => {
        const updateCount = reduceMotion ? 1 : 1 + Math.floor(Math.random() * 2);
        const next = current.slice();
        for (let n = 0; n < updateCount; n += 1) {
          const targetIndex = Math.floor(Math.random() * next.length);
          const seed = ROW_SEEDS[targetIndex];
          pulseCounter.current += 1;
          next[targetIndex] = seedRow(seed, pulseCounter.current);
        }
        return next;
      });
      setTick((value) => value + 1);
      setFramesSeen((value) => value + 14 + Math.floor(Math.random() * 7));
      setFps((value) => {
        const drift = (Math.random() - 0.5) * 14;
        const proposed = Math.max(880, Math.min(1480, value + drift));
        return Math.round(proposed);
      });
    }, reduceMotion ? 1200 : 320);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [paused]);

  return (
    <div className="hero-stream" aria-label="Simulated SmartCAN live frame stream">
      <div className="terminal-bar">
        <span></span>
        <span></span>
        <span></span>
        <strong>Live CAN Monitor</strong>
        <span className={`stream-status ${paused ? 'paused' : 'live'}`} aria-live="polite">
          <span className="stream-status-dot" aria-hidden="true"></span>
          {paused ? 'paused' : 'streaming'}
        </span>
      </div>
      <div className="stream-metrics">
        <div>
          <small>FPS</small>
          <strong>{fps.toLocaleString()}</strong>
        </div>
        <div>
          <small>Frames</small>
          <strong>{(framesSeen / 1_000_000).toFixed(2)}M</strong>
        </div>
        <div>
          <small>Known signals</small>
          <strong>{rows.filter((row) => row.known).length}</strong>
        </div>
        <div>
          <small>Tick</small>
          <strong>{tick.toString().padStart(4, '0')}</strong>
        </div>
      </div>
      <div className="stream-table-wrap" data-paused={paused ? 'true' : 'false'}>
        <table className="can-table stream-table">
          <thead>
            <tr>
              <th>CAN ID</th>
              <th>DLC</th>
              <th>DATA</th>
              <th>FREQ</th>
              <th>SIGNAL</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={`${row.canId}-${row.pulseKey}`}
                className={`stream-row ${row.known ? 'is-known' : ''}`}
              >
                <td className="mono">{row.canId}</td>
                <td className="mono">{row.dlc}</td>
                <td className="mono data-cell">{row.data}</td>
                <td className="mono dim">{row.freq}</td>
                <td>
                  {row.known ? (
                    <span className="known-signal-badge">{row.signalLabel ?? 'Known signal'}</span>
                  ) : (
                    <span className="unknown-signal">unmapped</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="stream-footer">
        <span className="stream-hint">Simulated stream — illustrative only.</span>
        <button
          type="button"
          className="stream-toggle"
          onClick={() => setPaused((value) => !value)}
          aria-pressed={paused}
        >
          {paused ? 'Resume' : 'Pause'}
        </button>
      </div>
    </div>
  );
}
